import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import i18n from '@/libs/i18n'

export default function useUsersList() {
    const toast = useToast()
    const refUserListTable = ref(null)
    const perPage = ref(10)
    const totalUsers = ref(0)
    const currentPage = ref(1)
    const perPageOptions = [10, 25, 50, 100]
    const searchQuery = ref('')
    const sortBy = ref('id')
    const isSortDirDesc = ref(true)
    const roleFilter = ref(null)
    const groupFilter = ref(null)
    const statusFilter = ref(null)
    const roleOptions = ref([])
    const groupOptions = ref([])
    const tableColumns = [
        { key: 'name', label: i18n.t('User'), sortable: true },
        { key: 'email', label: i18n.t('Email'), sortable: true },
        { key: 'roles', label: i18n.t('Rol'), },
        { key: groupOptions.length > 1 ? 'groups' : '', label: i18n.t('Groups'), },
        { key: 'status', label: i18n.t('Status'), sortable: true },
        { key: 'actions', label: i18n.t('Actions') },
    ]

    const dataMeta = computed(() => {
        const localItemsCount = refUserListTable.value ? refUserListTable.value.localItems.length : 0
        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: totalUsers.value,
        }
    })

    const refetchData = () => {
        refUserListTable.value.refresh()
    }

    watch([currentPage, perPage, searchQuery, roleFilter, groupFilter, statusFilter], () => {
        refetchData()
    })

    const fetchUsers = (ctx, callback) => {
        store
            .dispatch('users/fetchUsers', {
                q: searchQuery.value,
                perPage: perPage.value,
                page: currentPage.value,
                sortBy: sortBy.value,
                sortDesc: isSortDirDesc.value,
                role: roleFilter.value,
                group: groupFilter.value,
                status: statusFilter.value,
            })
            .then(response => {
                const { users, total, roles, groups } = response.data
                callback(users)
                roleOptions.value = roles
                groupOptions.value = groups
                tableColumns.value = [
                    { key: 'name', label: i18n.t('User'), sortable: true },
                    { key: 'email', label: i18n.t('Email'), sortable: true },
                    { key: 'roles', label: i18n.t('Rol'), },
                    { key: groupOptions.length > 1 ? 'groups' : '', label: i18n.t('Groups'), },
                    { key: 'status', label: i18n.t('Status'), sortable: true },
                    { key: 'actions', label: i18n.t('Actions') },
                ]
            
                totalUsers.value = total
            })
            .catch(() => {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Error fetching users list',
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                    },
                })
            })
    }

    // *===============================================---*
    // *--------- UI ---------------------------------------*
    // *===============================================---*

    const resolveUserRoleVariant = role => {
        if (role === 'Administrador') return 'primary'
        if (role === 'Superadministrador') return 'danger'
        if (role === 'Empleado') return 'info'
        return 'primary'
    }

    const resolveUserRoleIcon = role => {
        if (role === 'Administrador') return 'ServerIcon'
        if (role === 'Superadministrador') return 'SettingsIcon'
        if (role === 'Empleado') return 'UserIcon'
        return 'UserIcon'
    }

    const resolveUserStatusVariant = status => {
        if (status === true) return 'success'
        if (status === false) return 'secondary'
        return 'primary'
    }

    return {
        fetchUsers,
        tableColumns,
        perPage,
        currentPage,
        totalUsers,
        dataMeta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        refUserListTable,

        resolveUserRoleVariant,
        resolveUserRoleIcon,
        resolveUserStatusVariant,
        refetchData,

        // Extra Filters
        roleFilter,
        groupFilter,
        statusFilter,
        roleOptions,
        groupOptions
    }
}
